import * as React from 'react'
import * as PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { Provider, connect } from 'react-redux'
import { Switch } from 'react-router'
import { GoogleOAuthProvider } from '@react-oauth/google'

import ReactModal from 'react-modal'

import 'normalize.css'
import './assets/css/global.css'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import IdleTimeout from './components/session/IdleTimeout/containers/IdleTimeout'
import CookiePolicyModal from './components/home/CookiePolicyModal/CookiePolicyModal'
import ControlledRoute from './components/routing/ControlledRoute/ControlledRoute'
import IsomorphicRouter from './components/routing/IsomorphicRouter/IsomorphicRouter'
import IsomorphicContext from './contexts/IsomorphicContext'
import FirebaseContext from './contexts/FirebaseContext'
import Firebase from './components/accessibility/Firebase/Firebase'

import SimplifiedLogin from './views/simplified.login/containers/MainView'
import SimplifiedLoginOTP from './views/simplified.login.otp/containers/MainView'
import SimplifiedRegister from './views/simplified.register/containers/MainView'
import SimplifiedRegisterOTP from './views/simplified.register.otp/containers/MainView'
import SimplifiedRegisterSuccess from './views/simplified.register.success/containers/MainView'
import SimplifiedRegisterDetails from './views/simplified.register.details/containers/MainView'
import Profile from './views/profile/containers/MainView'
import RegisterAvatar from './views/register.avatar/containers/MainView'
import RegisterAddCard from './views/register.add-card/containers/MainView'
import RegisterPreferences from './views/register.preferences/containers/MainView'
import AccountRecovery from './views/account-recovery/containers/MainView'
import AccountRecoveryOtp from './views/account-recovery.otp/containers/MainView'
import Dashboard from './views/dashboard/containers/MainView'
import PromosAndEvents from './views/dashboard.promos-and-events/containers/MainView'
// import PromosAndEventsArticle from './views/dashboard.promos-and-events.article/containers/MainView'
import Explore from './views/dashboard.explore/containers/MainView'
import ExploreViewAll from './views/dashboard.explore.viewall/containers/MainView'
import MallDirectory from './views/dashboard.explore.mall-directory/containers/MainView'
import TradeHall from './views/dashboard.trade-hall/containers/MainView'
import LeasingInquiry from './views/leasing-inquiry/containers/MainView'
// import Rewards from './views/dashboard.rewards/containers/MainView'

// ST-7889 - [Web] - High Sessions on /watch Page with No Content
// import Watch from './views/dashboard.watch/containers/MainView'

import MerchantsCatalog from './views/dashboard.rewards.merchants-catalog/containers/MainView'
import RewardsCatalog from './views/dashboard.rewards.rewards-catalog/containers/MainView'
import eConcierge from './views/eConcierge/containers/MainView'
import eConciergeViewAll from './views/eConcierge.viewall/containers/MainView'
import eFoodChoices from './views/eFood-choices/containers/MainView'
import eFoodChoicesViewAll from './views/eFood-choices.viewall/containers/MainView'
import shopOnline from './views/shop-online/containers/MainView'
import OffersCatalog from './views/dashboard.rewards.offers-catalog/containers/MainView'
import WayfindingRoutes from './views/dashboard.wayfinding/containers/MainView'
import EmailVerification from './views/email-verification/containers/MainView'
import NotFound from './views/notfound/containers/MainView'
// import Tnc from './views/tnc/containers/MainView'
import WebViewCardConsent from './views/webview.cardconsent/components/MainView/MainView'
import WebViewGCashConsent from './views/webview.gcashconsent/components/MainView/MainView'
import WebViewPrivacyNotice from './views/webview.privacy-notice/components/MainView/MainView'
import WebViewWebsitePrivacyNotice from './views/webview.privacy-notice/components/WebsitePrivacy/MainView'
import EventsPrivacyNotice from './views/events.privacy-notice/components/MainView/MainView'
import WebViewTermsAndConditions from './views/webview.terms-and-conditions/components/MainView/MainView'
import WebViewTnCAndPNConsent from './views/webview.tnc-and-pn-consent/components/MainView/MainView'
import WebViewGCashReferral from './views/webview.gcash-referral-promo/MainView'
import Preview from './views/preview/components/MainView/MainView'
import MallPage from './views/page.mall/containers/MainView'
import PopupModal from './views/popup-modal/containers/MainView'
// import SiteDown from './views/app-maintenance/containers/MainView'
import SSOLoginVerify from './views/login.verify/containers/MainView'

//account deletion
import DeleteAccount from './views/delete-account/containers/MainView'
import DeleteAccountOtp from './views/delete-account.otp/containers/MainView'
import DeleteAccountSuccess from './views/delete-account.success/components/MainView/MainView'

import getUnauthenticatedRedirect from './services/redirection/getUnauthenticatedRedirect'

import { deleteAccessToken } from './services/data/accessToken'
import generateDeviceId from './services/generateDeviceId'

import {
  clearPrimaryInfo,
  setAccountStatus,
  setPreferenceIds,
} from './modules/profile/actions/creators'

import { setDevice } from './modules/device/actions/creators'

import {
  setAccessToken,
  // setFirebaseMessagingToken,
} from './modules/auth/actions/creators'

import {
  setGAUserId,
  setGAUserProperties,
  initializeGA,
} from './services/analytics/google-analytics'
import { deleteUuid } from './services/data/webUuid'
import {
  setGoogleToken,
  setAppleToken,
} from './modules/registration/actions/creators'
import { deleteSSOData } from './services/data/sso'
import { doPostLogout } from './controllers/Login'
import tiktokPixelScript from './static/tiktokPixel.js'
// import metaPixelScript from './static/metaPixel.js'
import { withMappedinKeys } from './services/hooks/useMappedinKeys'
// import { postLogout } from './services/api/requests/Login'

try {
  ReactModal.setAppElement('#root')
} catch (e) {}

function App(props) {
  const { history, store, name, context, server, url, ...theProps } = props

  const doLogout = React.useCallback(async () => {
    await doPostLogout()
    deleteAccessToken()
    deleteSSOData()
    deleteUuid()
    store.dispatch(clearPrimaryInfo())
    store.dispatch(setPreferenceIds(null))
    store.dispatch(setGoogleToken(null))
    store.dispatch(setAppleToken(null))
    store.dispatch(setAccessToken(null))
    store.dispatch(setAccountStatus(null))
    store.dispatch(setDevice(null))
    // store.dispatch(setFirebaseMessagingToken(null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const logoutCallback = React.useCallback(
    event => {
      const { newValue, oldValue, key } = event
      if (key === 'accessToken' && newValue === null && newValue !== oldValue) {
        doLogout()
        history.replace('/login')
      }
    },
    [doLogout, history],
  )

  React.useEffect(() => {
    if (window) {
      window.addEventListener('storage', logoutCallback)
    }
    return () => {
      if (window) {
        window.removeEventListener('storage', logoutCallback)
      }
    }
  }, [logoutCallback])

  const Logout = connect()(({ history: historyState }) => {
    doLogout()
    historyState.replace('/login')

    return ''
  })

  let prevAccessToken = ''
  let prevAdvanceAccount = null

  store.subscribe(() => {
    const { auth, profile, primaryInfo } = store.getState()
    const { accessToken } = auth || {}
    const { advanceAccount } = profile || {}
    const { customerId } = primaryInfo || {}

    if (JSON.stringify(advanceAccount) !== JSON.stringify(prevAdvanceAccount)) {
      if (advanceAccount === null) {
        setGAUserProperties({
          customer_id: '',
          device_id: generateDeviceId(),
          account_type: 'guest',
        })
      } else {
        setGAUserProperties({
          customer_id: customerId,
          device_id: generateDeviceId(),
          account_type: advanceAccount.advanced ? 'advanced' : 'basic',
        })
      }

      prevAdvanceAccount = advanceAccount
    }

    if (accessToken !== prevAccessToken) {
      setGAUserId(accessToken)

      if (!accessToken && prevAccessToken !== '') {
        // initializeGA(process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_CODE)
        initializeGA(process.env.REACT_APP_GA4_TRACKING_CODE)
      }

      prevAccessToken = accessToken
    }
  })

  return (
    <IsomorphicContext.Provider
      value={{
        server,
        ...theProps,
      }}
    >
      <Provider store={store}>
        <FirebaseContext.Provider value={new Firebase()}>
          <GoogleOAuthProvider
            clientId={process.env.REACT_APP_GOOGLE_SIGNIN_CLIENT_ID}
          >
            <>
              <Helmet defaultTitle={name} titleTemplate={`${name} | %s`}>
                <script id="tiktok-pixel-script">{tiktokPixelScript}</script>
                <meta name="referrer" content="strict-origin" />
                {/* <script id="meta-pixel-script">{metaPixelScript}</script> */}
              </Helmet>
              <IsomorphicRouter
                location={url}
                history={history}
                context={context}
              >
                <>
                  <Switch>
                    <ControlledRoute
                      path="/webview/cardconsent"
                      component={WebViewCardConsent}
                    />
                    <ControlledRoute
                      path="/webview/gcashconsent"
                      component={WebViewGCashConsent}
                    />
                    <ControlledRoute
                      path="/webview/tnc-and-pn-consent"
                      component={WebViewTnCAndPNConsent}
                    />
                    <ControlledRoute
                      path="/webview/terms-and-conditions"
                      component={WebViewTermsAndConditions}
                    />
                    <ControlledRoute
                      path="/webview/privacy-notice"
                      component={WebViewPrivacyNotice}
                    />
                    <ControlledRoute
                      path="/webview/ayalamalls-website-privacy"
                      component={WebViewWebsitePrivacyNotice}
                    />
                    <ControlledRoute
                      path="/events-PrivacyNotice"
                      component={EventsPrivacyNotice}
                    />
                    <ControlledRoute
                      path="/webview/gcash-x-zing-referral-promo"
                      component={WebViewGCashReferral}
                    />
                    <ControlledRoute
                      path="/webview/wayfinding/:mallSlug?/:actionType?/:actionData?/:addonType?/:addonData?/:extraData?"
                      webview
                      baseUrl="/webview/wayfinding"
                      component={WayfindingRoutes}
                    />
                    <ControlledRoute
                      path="/tpa-redirect"
                      component={() => <></>}
                    />
                    <ControlledRoute
                      path="/page/mall/:viewId"
                      component={MallPage}
                    />
                    <ControlledRoute
                      path="/preview/:component"
                      exact
                      component={Preview}
                    />
                    {/* DELETE ACCOUNT */}
                    <ControlledRoute
                      path="/delete-account/otp"
                      baseUrl="/delete-account"
                      component={DeleteAccountOtp}
                    />
                    <ControlledRoute
                      path="/delete-account/success"
                      baseUrl="/delete-account"
                      component={DeleteAccountSuccess}
                    />
                    <ControlledRoute
                      path="/delete-account"
                      component={DeleteAccount}
                    />
                    <>
                      <Switch>
                        <ControlledRoute
                          exact
                          path="/login/sso"
                          component={SSOLoginVerify}
                        />
                        <ControlledRoute
                          exact
                          path="/login"
                          component={SimplifiedLogin}
                          redirect={getUnauthenticatedRedirect}
                        />
                        <ControlledRoute
                          exact
                          path="/login/otp"
                          component={SimplifiedLoginOTP}
                          redirect={getUnauthenticatedRedirect}
                        />
                        <ControlledRoute
                          exact
                          path="/register"
                          component={SimplifiedRegister}
                          redirect={getUnauthenticatedRedirect}
                        />
                        <ControlledRoute
                          path="/register/details"
                          component={SimplifiedRegisterDetails}
                          redirect={getUnauthenticatedRedirect}
                        />
                        <ControlledRoute
                          path="/register/otp"
                          component={SimplifiedRegisterOTP}
                          redirect={getUnauthenticatedRedirect}
                        />
                        <ControlledRoute
                          path="/register/avatar"
                          component={RegisterAvatar}
                          redirect={getUnauthenticatedRedirect}
                        />
                        <ControlledRoute
                          path="/register/add-card"
                          component={RegisterAddCard}
                          redirect={getUnauthenticatedRedirect}
                        />
                        <ControlledRoute
                          path="/register/preferences"
                          component={RegisterPreferences}
                          redirect={getUnauthenticatedRedirect}
                        />
                        <ControlledRoute
                          path="/register/success"
                          component={SimplifiedRegisterSuccess}
                          redirect={getUnauthenticatedRedirect}
                        />
                        {/* <ControlledRoute
                          path="/register/referral/:referralCode?"
                          component={RegisterReferral}
                          redirect={getUnauthenticatedRedirect}
                        /> */}
                        <ControlledRoute
                          exact
                          path="/account-recovery"
                          component={AccountRecovery}
                          redirect={getUnauthenticatedRedirect}
                        />
                        <ControlledRoute
                          exact
                          path="/account-recovery/otp"
                          component={AccountRecoveryOtp}
                          redirect={getUnauthenticatedRedirect}
                        />
                        <ControlledRoute
                          path="/logout"
                          exact
                          component={Logout}
                        />
                        {/* <ControlledRoute
                        path="/promos-and-events/:viewType/:viewId"
                        baseUrl="/promos-and-events"
                        component={PromosAndEventsArticle}
                      /> */}
                        <ControlledRoute
                          path="/promos-and-events/:viewType?/:viewId?"
                          baseUrl="/promos-and-events"
                          component={PromosAndEvents}
                        />
                        <ControlledRoute
                          path="/trade-hall"
                          baseUrl="/trade-hall"
                          component={TradeHall}
                        />
                        <ControlledRoute
                          exact
                          path="/leasing-inquiry"
                          baseUrl="/leasing-inquiry"
                          component={LeasingInquiry}
                        />
                        <ControlledRoute
                          path="/explore/mall-directory"
                          baseUrl="/explore/mall-directory"
                          component={MallDirectory}
                        />
                        <ControlledRoute
                          path="/explore/:mallSlug/view-all/:viewType/:viewId?"
                          baseUrl="/explore"
                          component={ExploreViewAll}
                        />
                        <ControlledRoute
                          path="/explore/:mallSlug?/:viewType?/:viewId?"
                          baseUrl="/explore"
                          component={withMappedinKeys(Explore)}
                        />
                        <ControlledRoute
                          path="/rewards/catalog/merchants/:mallSlug/:viewId?"
                          baseUrl="/rewards/catalog/merchants"
                          component={MerchantsCatalog}
                        />
                        <ControlledRoute
                          path="/rewards/catalog/rewards/:viewId?"
                          baseUrl="/rewards/catalog/rewards"
                          component={RewardsCatalog}
                        />
                        <ControlledRoute
                          path="/econcierge/:mallSlug/view-all/:viewType/:viewId?"
                          baseUrl="/econcierge"
                          component={eConciergeViewAll}
                        />
                        <ControlledRoute
                          path="/econcierge"
                          baseUrl="/econcierge"
                          component={eConcierge}
                        />
                        <ControlledRoute
                          // path="/efood-choices/:mallSlug/view-all/:viewType"
                          path="/zing-eats/:mallSlug/view-all/:viewType"
                          baseUrl="/zing-eats"
                          component={eFoodChoicesViewAll}
                        />
                        <ControlledRoute
                          path="/zing-eats/:activeTab?/:mallSlug?"
                          baseUrl="/zing-eats"
                          component={eFoodChoices}
                        />
                        <ControlledRoute
                          // path="/shop-online"
                          // baseUrl="/shop-online"
                          path="/zing-shops"
                          baseUrl="/zing-shops"
                          component={shopOnline}
                        />
                        <ControlledRoute
                          path="/rewards/catalog/offers/:viewId?"
                          baseUrl="/rewards/catalog/offers"
                          component={OffersCatalog}
                        />
                        {/*
                        ST-7046 - Rewards :: Advanced : Remove Rewards Page
                         <ControlledRoute
                          path="/rewards/:viewType?/:viewId?"
                          baseUrl="/rewards"
                          component={Rewards}
                        /> */}
                        {/* // ST-7889 - [Web] - High Sessions on /watch Page with No Content */}
                        {/* <ControlledRoute
                          path="/watch/:watchSlug?/:viewType?/:viewId?/:theater?/:date?/:cinema?/:time?"
                          baseUrl="/watch"
                          component={Watch}
                        /> */}
                        <ControlledRoute
                          path="/wayfinding/:mallSlug?/:actionType?/:actionData?/:addonType?/:addonData?/:extraData?"
                          baseUrl="/wayfinding"
                          component={WayfindingRoutes}
                        />
                        <ControlledRoute
                          path="/email-verification/:token?"
                          baseUrl="/email-verification"
                          component={EmailVerification}
                        />
                        <ControlledRoute
                          path="/"
                          baseUrl="/main"
                          exact
                          component={Dashboard}
                        />
                        <ControlledRoute
                          path="/main/:viewType?/:viewId?"
                          baseUrl="/main"
                          component={Dashboard}
                        />
                        {/* <ControlledRoute
                        exact
                        path="/app-maintenance"
                        component={SiteDown}
                      /> */}
                        <ControlledRoute path="/" component={NotFound} />
                      </Switch>
                      <ControlledRoute exact path="/" component={PopupModal} />
                      <ControlledRoute
                        path="/:page?/:pageType?"
                        component={Profile}
                      />
                      <CookiePolicyModal />
                      <IdleTimeout
                        timeout={
                          parseInt(process.env.REACT_APP_TIMEOUT, 10) || 0
                        }
                        timeoutNotice={
                          parseInt(process.env.REACT_APP_TIMEOUT_NOTICE, 10) ||
                          0
                        }
                        onTimeout={doLogout}
                        startOnMount
                      />
                    </>
                  </Switch>
                </>
              </IsomorphicRouter>
            </>
          </GoogleOAuthProvider>
        </FirebaseContext.Provider>
      </Provider>
    </IsomorphicContext.Provider>
  )
}

App.defaultProps = {
  location: null,
  server: false,
}

App.propTypes = {
  history: PropTypes.shape().isRequired,
  store: PropTypes.shape().isRequired,
  location: PropTypes.string,
  name: PropTypes.string.isRequired,
  context: PropTypes.shape(),
  server: PropTypes.bool,
}

export default App
